import { ref, computed } from '@vue/composition-api'
import { apiToastSuccess, apiToastWarning, apiToastError } from '@/@core/utils/toast'
import constants from '@/constants'
import analytics from '@/@core/utils/analytics'
import { formatObject, getSizeInKb } from '@/@core/utils/utils'
import store from '@/store'
import { CREATE_PARTIAL_SHIPMENT, EXPORT_SHIPMENT, IMPORT_SHIPMENT } from '@/store/modules/shipment.module'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'

const { ORDER_PRODUCT_SHIPMENT_STATUS } = constants
const SHIPMENT_SETTINGS_MODAL_ID = 'modal_shipment_settings'
const MAX_DISPLAYED_ERRORS = 10

export function useShipmentCreation() {
  const shipmentOrder = ref(null)
  const isShipping = ref(false)
  const totalOrderProductsCount = ref(0)
  const createShipmentWarningMessage = ref('')
  const quantityErrors = ref([])
  const showAllErrors = ref(false)
  const shipmentImportFile = ref(null)
  const isExportingShipment = ref(false)
  const isImportingShipment = ref(false)
  const isImportExportOpen = ref(false)
  const shipmentOrderKey = ref(0)

  const displayedQuantityErrors = computed(() => {
    if (showAllErrors.value) {
      return quantityErrors.value
    }
    return quantityErrors.value.slice(0, MAX_DISPLAYED_ERRORS)
  })

  const remainingErrorsCount = computed(() => {
    if (showAllErrors.value) {
      return 0
    }
    return Math.max(0, quantityErrors.value.length - MAX_DISPLAYED_ERRORS)
  })

  function onClickShipNow(shipmentOrderData, totalProducts = 0) {
    totalOrderProductsCount.value = totalProducts
    shipmentOrder.value = shipmentOrderData
    // Component should show the shipment settings modal
  }

  // Build shipment payload
  function buildCartonPayload(product) {
    const quantity = product.quantity || 0
    return quantity > 0 ? { productId: product.productId, quantity } : null
  }

  function buildVariantPayload(product) {
    const items = product.itemsForShipment || product.items
    const shipmentItems = items
      .filter(item => (item.value || 0) > 0)
      .map(item => ({ skuId: item.skuId || item.sku, value: item.value || 0 }))

    return shipmentItems.length > 0
      ? { productId: product.productId, items: shipmentItems }
      : null
  }

  function buildProductPayload(product) {
    return product.isCarton
      ? buildCartonPayload(product)
      : buildVariantPayload(product)
  }

  function buildShipmentProducts() {
    return shipmentOrder.value.products
      .filter(product => product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL)
      .map(product => buildProductPayload(product))
      .filter(Boolean)
  }

  // Validation
  function validateCartonQuantity(product) {
    const hasExceededMax = product.maxShipmentQty !== undefined
      && product.quantity > product.maxShipmentQty
    if (!hasExceededMax) return null
    return {
      sku: product.name || product.productId,
      requested: product.quantity,
      available: product.maxShipmentQty,
    }
  }

  function validateItemQuantities(product) {
    const items = product.itemsForShipment || product.items || []
    return items
      .filter(item => item.maxShipmentQty !== undefined && (item.value || 0) > item.maxShipmentQty)
      .map(item => ({
        sku: item.skuId || item.sku,
        requested: item.value || 0,
        available: item.maxShipmentQty,
      }))
  }

  function validateShipmentQuantities() {
    const selectedProducts = shipmentOrder.value.products.filter(
      product => product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL,
    )
    return selectedProducts.flatMap(product => {
      if (product.isCarton) {
        const error = validateCartonQuantity(product)
        return error ? [error] : []
      }
      return validateItemQuantities(product)
    })
  }

  // Error handling
  function parseQuantityErrors(errors) {
    const parsedErrors = []
    const regex = /SKU\s*"([^"]+)"\s*requested\s*(\d+)\s*but\s*only\s*(\d+)\s*remaining/i

    errors.forEach(error => {
      const match = error.match(regex)
      if (match) {
        parsedErrors.push({
          sku: match[1],
          requested: parseInt(match[2], 10),
          available: parseInt(match[3], 10),
        })
      }
    })

    return parsedErrors
  }

  function isCreditLimitError(err, responseData) {
    return err.response?.status === 422 && responseData?.data?.creditLimitExceeded
  }

  function hasValidationErrors(responseData) {
    return responseData?.errors && Array.isArray(responseData.errors)
  }

  function isEmptyProductsError(errors) {
    return errors.some(e => e?.message?.includes('products must NOT have fewer than 1'))
  }

  function extractErrorStrings(errors) {
    return errors.map(e => (typeof e === 'string' ? e : e?.message)).filter(Boolean)
  }

  function getErrorMessage(err) {
    return err.response?.data?.message || err.message || 'Failed to create shipment'
  }

  function handleValidationErrors(responseData) {
    if (isEmptyProductsError(responseData.errors)) {
      apiToastWarning('Please add quantity to at least one item before creating a shipment')
      return { showQuantityErrorModal: false }
    }

    const errorStrings = extractErrorStrings(responseData.errors)
    quantityErrors.value = parseQuantityErrors(errorStrings)
    showAllErrors.value = false

    if (quantityErrors.value.length > 0) {
      return { showQuantityErrorModal: true }
    }
    const fallbackMessage = responseData.message || errorStrings[0] || 'Validation failed'
    apiToastWarning(fallbackMessage)
    return { showQuantityErrorModal: false }
  }

  function handleShipmentSuccess(payload) {
    isShipping.value = false
    shipmentOrder.value.products.forEach(product => {
      if (product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL) {
        product.status = ORDER_PRODUCT_SHIPMENT_STATUS.SHIPPED.LABEL
      }
    })
    apiToastSuccess('Shipment created successfully')
    analytics.track(
      constants.TRACKS.ACTIONS.RETAILER_CREATES_SHIPMENT,
      formatObject({
        noOfProductsInOrder: totalOrderProductsCount.value,
        noOfProductsInShipment: payload.products?.length,
      }),
    )
    return { success: true }
  }

  function handleShipmentError(err) {
    isShipping.value = false
    const responseData = err.response?.data

    if (isCreditLimitError(err, responseData)) {
      createShipmentWarningMessage.value = responseData?.data?.message
      return { success: false, showCreditLimitModal: true }
    }

    if (hasValidationErrors(responseData)) {
      const result = handleValidationErrors(responseData)
      return { success: false, ...result }
    }

    apiToastWarning(getErrorMessage(err))
    return { success: false }
  }

  function confirmShipment() {
    const validationErrors = validateShipmentQuantities()
    if (validationErrors.length > 0) {
      quantityErrors.value = validationErrors
      showAllErrors.value = false
      return { success: false, showQuantityErrorModal: true }
    }

    isShipping.value = true
    const products = buildShipmentProducts()
    const payload = { orderId: shipmentOrder.value._id, products }

    return store.dispatch(CREATE_PARTIAL_SHIPMENT, payload)
      .then(() => {
        const result = handleShipmentSuccess(payload)
        return result
      })
      .catch(err => handleShipmentError(err))
  }

  function cancelShipment() {
    if (isImportExportOpen.value) {
      return
    }
    shipmentOrder.value = null
  }

  // Import/Export
  function openImportExportModal() {
    isImportExportOpen.value = true
  }

  function closeImportExportModal() {
    isImportExportOpen.value = false
  }

  function onShipmentFileChange(event) {
    const file = event.target.files[0]
    if (file) {
      shipmentImportFile.value = file
    }
  }

  function removeShipmentFile(fileInputRef) {
    shipmentImportFile.value = null
    if (fileInputRef) {
      fileInputRef.value = ''
    }
  }

  async function exportShipment() {
    if (!shipmentOrder.value) return

    isExportingShipment.value = true
    const productIds = shipmentOrder.value.products
      .map(product => product.productId)
      .filter(Boolean)

    const payload = {
      orderId: shipmentOrder.value._id,
      productIds,
    }

    try {
      const res = await store.dispatch(EXPORT_SHIPMENT, payload)
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Shipment-${shipmentOrder.value.orderSeqId}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      apiToastSuccess('Shipment exported successfully')
    } catch (err) {
      apiToastError(err)
    } finally {
      isExportingShipment.value = false
    }
  }

  function applyProductQuantity(product, importedProduct) {
    if (product.isCarton && importedProduct.quantity !== undefined) {
      product.quantity = importedProduct.quantity
      return
    }

    if (!importedProduct.items || !product.itemsForShipment) return

    importedProduct.items.forEach(importedItem => {
      const item = product.itemsForShipment.find(
        i => (i.skuId || i.sku) === importedItem.skuId,
      )
      if (item) {
        item.value = importedItem.value
      }
    })
  }

  function applyImportedQuantities(importedProducts) {
    if (!importedProducts || !shipmentOrder.value) return

    importedProducts.forEach(importedProduct => {
      const product = shipmentOrder.value.products.find(
        p => p.productId === importedProduct.productId,
      )
      if (product) {
        applyProductQuantity(product, importedProduct)
      }
    })
    shipmentOrderKey.value += 1
  }

  async function processShipmentImport() {
    const fileUrl = await store.dispatch(UPLOAD_DOCUMENTS, {
      file: shipmentImportFile.value,
      fileType: 'shipment-import',
    })

    const payload = { orderId: shipmentOrder.value._id, fileUrl }
    const res = await store.dispatch(IMPORT_SHIPMENT, payload)

    applyImportedQuantities(res.data.data.products)

    const successMessage = res.data?.message || res.message || 'Shipment imported successfully'
    apiToastSuccess(successMessage)
  }

  async function importShipment() {
    if (!shipmentImportFile.value || !shipmentOrder.value) return { success: false }

    isImportingShipment.value = true

    try {
      await processShipmentImport()
      return { success: true }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to import shipment'
      apiToastError(errorMessage)
      return { success: false }
    } finally {
      isImportingShipment.value = false
    }
  }

  return {
    // State
    shipmentOrder,
    isShipping,
    totalOrderProductsCount,
    createShipmentWarningMessage,
    quantityErrors,
    showAllErrors,
    shipmentImportFile,
    isExportingShipment,
    isImportingShipment,
    isImportExportOpen,
    shipmentOrderKey,

    // Computeds
    displayedQuantityErrors,
    remainingErrorsCount,

    // Methods
    onClickShipNow,
    confirmShipment,
    cancelShipment,
    openImportExportModal,
    closeImportExportModal,
    onShipmentFileChange,
    removeShipmentFile,
    exportShipment,
    importShipment,

    // Constants
    SHIPMENT_SETTINGS_MODAL_ID,
    MAX_DISPLAYED_ERRORS,
    getSizeInKb,
  }
}
